import { useEffect, useMemo, useState } from 'react';
import { MessageCircle, Users, UserX, Cake, CalendarDays, Check, Phone, RotateCcw } from 'lucide-react';
import { getAudience, AudienceMember, AudienceType, TEMPLATES } from '../api';
import { listClasses, ClassRow } from '../../classes/api';
import PersonAvatar from '../../../components/PersonAvatar';
import { getWhatsAppLink, normalizeWa, fillTemplate } from '../../../utils/whatsapp';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';

const AUDIENCES: { key: AudienceType; label: string; icon: typeof Users; hint: string }[] = [
  { key: 'active', label: 'Todos los alumnos', icon: Users, hint: 'activos' },
  { key: 'inactive', label: 'Inactivos', icon: UserX, hint: 'sin pago en 60 días' },
  { key: 'birthdays', label: 'Cumpleañeros', icon: Cake, hint: 'próximos 14 días' },
  { key: 'class', label: 'Por clase', icon: CalendarDays, hint: 'roster de una clase' },
];

// Difusión por WhatsApp click-to-chat (uno por uno) a una audiencia, con plantillas {nombre}.
export default function BroadcastPanel() {
  const [audience, setAudience] = useState<AudienceType>('active');
  const [classId, setClassId] = useState<number | null>(null);
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [members, setMembers] = useState<AudienceMember[] | null>(null);
  const [tplKey, setTplKey] = useState('pago');
  const [body, setBody] = useState(TEMPLATES[0].body);
  const [done, setDone] = useState<Set<number>>(new Set());

  useEffect(() => { listClasses().then((r) => setClasses(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (audience === 'class' && !classId) { setMembers([]); return; }
    setMembers(null);
    getAudience(audience, { class_id: classId || undefined })
      .then((r) => { setMembers(r.data); setDone(new Set()); })
      .catch(() => setMembers([]));
  }, [audience, classId]);

  function pickTemplate(key: string) {
    setTplKey(key);
    const t = TEMPLATES.find((x) => x.key === key);
    if (t) setBody(t.body);
  }

  const withPhone = useMemo(() => (members || []).filter((m) => normalizeWa(m.phone_prefix, m.phone_number)), [members]);
  const preview = withPhone[0] ? fillTemplate(body, { nombre: withPhone[0].firstname }) : fillTemplate(body, { nombre: 'Ana' });

  function openWa(m: AudienceMember) {
    const link = getWhatsAppLink(m.phone_prefix, m.phone_number, fillTemplate(body, { nombre: m.firstname }));
    if (!link) return;
    window.open(link, '_blank');
    setDone((prev) => new Set(prev).add(m.account_id));
  }

  return (
    <div>
      <p className="mb-4 text-sm text-content-3">Manda un aviso por WhatsApp a un grupo de alumnos, uno por uno (sin envío automático).</p>

      {/* 1. Audiencia */}
      <div className="mb-4">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-content-3">1 · ¿A quién?</div>
        <div className="flex flex-wrap gap-2">
          {AUDIENCES.map(({ key, label, icon: Icon, hint }) => (
            <button key={key} onClick={() => setAudience(key)}
              className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${
                audience === key ? 'border-accent bg-accent/10 text-content' : 'border-line bg-surface text-content-2 hover:bg-surface-2'}`}>
              <Icon size={16} className={audience === key ? 'text-brand-blue' : 'text-content-3'} />
              <span className="font-medium">{label}</span>
              <span className="text-xs text-content-3">· {hint}</span>
            </button>
          ))}
        </div>
        {audience === 'class' && (
          <select value={classId || ''} onChange={(e) => setClassId(Number(e.target.value) || null)}
            className="mt-2 w-full max-w-sm rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue">
            <option value="">Elige una clase…</option>
            {classes.map((c) => <option key={c.class_id} value={c.class_id}>{c.name}</option>)}
          </select>
        )}
      </div>

      {/* 2. Mensaje */}
      <div className="mb-4">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-content-3">2 · El mensaje</div>
        <div className="flex flex-wrap gap-1.5">
          {TEMPLATES.map((t) => (
            <button key={t.key} onClick={() => pickTemplate(t.key)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                tplKey === t.key ? 'bg-accent text-on-accent' : 'bg-surface-2 text-content-2 hover:bg-line'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <textarea value={body} onChange={(e) => { setBody(e.target.value); setTplKey('libre'); }}
          placeholder="Escribe tu mensaje. Usa {nombre} para personalizar."
          className="mt-2 min-h-[96px] w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue" />
        <div className="mt-1 flex items-center justify-between text-xs text-content-3">
          <span>Usa <code className="rounded bg-surface-2 px-1">{'{nombre}'}</code> y se reemplaza por cada alumno.</span>
          <span>{body.length} caracteres</span>
        </div>
        {body.trim() && (
          <div className="mt-2 rounded-lg border border-line bg-surface-2/50 p-3 text-sm text-content-2">
            <span className="text-xs font-medium text-content-3">Vista previa:</span>
            <p className="mt-1 whitespace-pre-wrap">{preview}</p>
          </div>
        )}
      </div>

      {/* 3. Destinatarios */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">3 · Enviar</div>
          {members && members.length > 0 && (
            <div className="flex items-center gap-3 text-xs text-content-3">
              <span>{done.size}/{withPhone.length} contactados</span>
              {done.size > 0 && (
                <button onClick={() => setDone(new Set())} className="inline-flex items-center gap-1 hover:text-content">
                  <RotateCcw size={12} /> reiniciar
                </button>
              )}
            </div>
          )}
        </div>

        {!members ? (
          <Skeleton className="h-64 w-full rounded-2xl" />
        ) : members.length === 0 ? (
          <EmptyState icon={MessageCircle} title="Sin destinatarios"
            subtitle={audience === 'class' ? 'Elige una clase con alumnos.' : 'No hay alumnos en esta audiencia.'} />
        ) : (
          <>
            {withPhone.length < members.length && (
              <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
                {members.length - withPhone.length} sin teléfono — no se les puede escribir por WhatsApp.
              </p>
            )}
            <div className="divide-y divide-line/60 overflow-hidden rounded-2xl border border-line bg-surface">
              {members.map((m) => {
                const wa = normalizeWa(m.phone_prefix, m.phone_number);
                const contacted = done.has(m.account_id);
                return (
                  <div key={m.account_id} className={`flex items-center gap-3 px-4 py-2.5 ${contacted ? 'bg-emerald-500/5' : ''}`}>
                    <PersonAvatar contactId={m.account_id} hasPhoto={m.has_photo}
                      name={m.firstname} lastName={m.lastname} size="h-8 w-8" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-content">{m.firstname} {m.lastname}</div>
                      {typeof m.days_since === 'number' && <div className="text-xs text-content-3">{m.days_since} días sin pagar</div>}
                      {typeof m.days_until === 'number' && <div className="text-xs text-content-3">cumple en {m.days_until} días</div>}
                    </div>
                    {wa ? (
                      <button onClick={() => openWa(m)}
                        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                          contacted
                            ? 'border border-emerald-500/40 text-emerald-600 dark:text-emerald-400'
                            : 'bg-emerald-500 text-white hover:bg-emerald-600'}`}>
                        {contacted ? <><Check size={13} /> Contactado</> : <><Phone size={13} /> WhatsApp</>}
                      </button>
                    ) : (
                      <span className="text-xs text-content-3">sin tel.</span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
